import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

export function useInfiniteVideos(params, pageSize = 12) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const state = useRef({ skip: 0, busy: false, gen: 0 });
  const key = JSON.stringify(params);

  const loadMore = useCallback(async () => {
    const st = state.current;
    if (st.busy || st.done) return;
    st.busy = true; setLoading(true);
    const gen = st.gen;
    try {
      const { data } = await api.get("/videos", { params: { ...JSON.parse(key), limit: pageSize, skip: st.skip } });
      if (gen !== st.gen) return;
      st.skip += data.length;
      setItems(prev => { const seen = new Set(prev.map(v => v.id)); return [...prev, ...data.filter(v => !seen.has(v.id))]; });
      if (data.length < pageSize) { st.done = true; setDone(true); }
    } catch { st.done = true; setDone(true); }
    finally { if (gen === st.gen) { st.busy = false; setLoading(false); } }
  }, [key, pageSize]);

  useEffect(() => {
    state.current = { skip: 0, busy: false, done: false, gen: state.current.gen + 1 };
    setItems([]); setDone(false); setLoading(false);
    loadMore();
  }, [loadMore]);

  const observer = useRef(null);
  const sentinelRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    if (!node) return;
    observer.current = new IntersectionObserver(es => { if (es[0].isIntersecting) loadMore(); }, { rootMargin: "600px" });
    observer.current.observe(node);
  }, [loadMore, items.length]);

  return { items, loading, done, sentinelRef };
}
